import { PresentationOption } from "./../vo/presentation-option";
import { ConfigDocModelService } from "./config-doc-model.service";
import { Injectable } from "@angular/core";
import { BaseModel } from "./base-model";
import { CourseStructure } from "../vo/course-structure";
import { CourseStructureModelService } from "./course-structure-model.service";
import { CourseContext } from "../vo/course-context";
import { CustomDoc } from "../vo/custom-doc";
import { AppConstants } from "app/core/vo/app-constants";
import { TrackingManager } from "app/core/tracking/tracking-manager";
import { ApplicationModel } from "app/core/models/application.model";
import { UtilityScripts } from "app/helpers/utility-scripts";
import { CMI5 } from "../tracking/cmi5";
import { StatementDataVO } from "app/rubrics/adaptive/vo/statementDataVO";

@Injectable()
export class CourseContextModelService extends BaseModel {
	private intCurrentPage: number = 0;
	private intTotalPages: number = 0;
	private pageId: string;
	private intCurrentPageOfTotalPagesInCourse: number = 0;
	private intTotalPagesInCourse: number = 0;
	private intCurrentScene: number = 0;
	private intCurrentModule: number = 0;
	private strCurrentPageId: string;
	private strCurrentSceneId: string;
	private strCurrentModuleId: string;
	private strCurrentLanguage: string = "english";
	private bAudioOn: boolean;
	private oCustomDoc: CustomDoc = null;
	private intTotalCompletedpagesCount: number = 0;
	private text_direction: String;
	//
	private maxModuleReached: number = 0;
	private maxSceneReached: number = 0;
	private maxPageReached: number = 0;
	//
	private registerJumptoPage = false;
	private isBackBtnClick = false;
	private isNextBtnClick = false;
	private intCurrentJumptoPageModule = 0;
	private intCurrentJumptoPageScene = 0;
	private intCurrentJumptoPagePage = 0;
	private _disableBackBtn = false;
	public courseData: any;
	//
	constructor(
		private courseStructureModel: CourseStructureModelService,
		private configDocModelService: ConfigDocModelService
	) {
		super();
	}

	public initialize(data: any) {
		this.initProperties(data);
	}

	private initProperties(data: any): void {
		this.intCurrentPage = this.maxModuleReached = data.pageNum;
		this.intCurrentScene = this.maxSceneReached = data.sceneNum;
		this.intCurrentModule = this.maxPageReached = data.moduleNum;
		this.intTotalCompletedpagesCount =
			TrackingManager.instance.opProfile === AppConstants.DEMO_MODE
				? this.totalPagesInCourse
				: 0;
		this.checkForSkippablePages();
		this.updateCoursePage();
	}

	public get courseStructure(): CourseStructure {
		return this.courseStructureModel.courseStructureVO;
	}

	public set currentPage(value: number) {
		this.intCurrentPage = value;
	}

	public get currentPage(): number {
		return this.intCurrentPage;
	}
	public set totalPages(value: number) {
		this.intTotalPages = value;
	}
	public get totalPages(): number {
		return this.courseStructureModel.getTotalPagesOfScene(
			this.intCurrentModule,
			this.intCurrentScene
		);
	}

	public set currentPageOfTotalPagesInCourse(value: number) {
		this.intCurrentPageOfTotalPagesInCourse = value;
	}
	public get currentPageOfTotalPagesInCourse(): number {
		return this.intCurrentPageOfTotalPagesInCourse;
	}

	public set totalPagesInCourse(value: number) {
		this.intTotalPagesInCourse = value;
	}
	public get totalPagesInCourse(): number {
		return this.courseStructureModel.getTotalPagesInCourse();
	}

	public set currentScene(value: number) {
		this.intCurrentScene = value;
	}
	public get currentScene(): number {
		return this.intCurrentScene;
	}

	public set currentModule(value: number) {
		this.intCurrentModule = value;
	}

	public get currentModule(): number {
		return this.intCurrentModule;
	}

	public set currentLanguage(value: string) {
		this.strCurrentLanguage = value;
	}

	public get currentLanguage(): string {
		return this.strCurrentLanguage;
	}

	public set audioOn(value: boolean) {
		this.bAudioOn = value;
	}

	public get audioOn(): boolean {
		return this.bAudioOn;
	}
	public set customDoc(value: CustomDoc) {
		this.oCustomDoc = value;
	}
	public get customDoc(): CustomDoc {
		return this.oCustomDoc;
	}
	public get customDocs(): CustomDoc[] {
		return this.configDocModelService.customDocs;
	}
	public get presentationModes(): PresentationOption[] {
		return this.configDocModelService.presentationModes;
	}

	public get selectedPresentationMode(): PresentationOption {
		return this.configDocModelService.presentationModes.find(
			(mode) => mode.selected === true
		);
	}

	public get courseTitles() {
		return this.configDocModelService.getCourseTitles();
	}

	public currentModuleTitle(): string {
		if (this.courseStructure.moduleStructures[this.currentModule]) {
			const mod = this.courseStructure.moduleStructures[this.currentModule];
			if (mod && mod.childScenes[this.currentScene].courseScene.scene_name) {
				return mod.childScenes[this.currentScene].courseScene.scene_name;
			} else if (mod) {
				return mod.courseModule.module_name;
			} else {
				return ``;
			}
		}
	}

	public get currentPageInfo() {
		return this.courseStructure.moduleStructures[this.intCurrentModule]
			.childScenes[this.intCurrentScene].childPages[this.intCurrentPage];
	}
	public getPageInfoById(pageid: string): any {
		let pageInfo: any;
		this.courseStructure.moduleStructures.forEach((mod, modIndex) => {
			mod.childScenes.forEach((sce, sceIndex) => {
				sce.childPages
					.filter((lPage) => lPage.page_language === this.strCurrentLanguage)
					.forEach((pge, pageIndex) => {
						if (pge.page_id === pageid) {
							pageInfo = {
								page_id: pge.page_id,
								page_path: pge.page_path,
								status: pge.status,
								page_index: pageIndex,
								scene_index: sceIndex,
								scene_status: sce.courseScene.status,
								scene_id: sce.courseScene.scene_id,
								module_index: modIndex,
								module_id: mod.courseModule.module_id,
								state_specific_content: pge.state_specific_content,
							};
						}
					});
			});
		});
		return pageInfo;
	}

	public setMaxVisitedPage() {
		let bookmark = TrackingManager.instance.trackingVO.bookmark;
		let modules = bookmark.split(";");
		let lastVisitedModName = "";
		let lastVisitedPageID = 0;
		for (let i = 0; i < modules.length; i++) {
			var modDetails = modules[i].split(",");
			if (modDetails[0] == "i") {
				lastVisitedModName = modDetails[2];
				this.maxPageReached = parseInt(modDetails[1]);
			}
		}
		for (let i = 0; i < this.courseStructure.moduleStructures.length; i++) {
			for (
				let j = 0;
				j < this.courseStructure.moduleStructures[i].childScenes.length;
				j++
			) {
				if (
					this.courseStructure.moduleStructures[i].childScenes[j].courseScene
						.scene_id == lastVisitedModName
				) {
					this.maxModuleReached = i;
					this.maxSceneReached = j;
				}
			}
		}
	}
	public updatePageById(pageid: string = "") {
		//console.log("updatePageById ",this.courseStructure.moduleStructures);
		if (pageid !== "") {
			this.courseStructure.moduleStructures.forEach((mod, modIndex) => {
				mod.childScenes.forEach((sce, sceIndex) => {
					sce.childPages
						.filter((cPage) => cPage.page_language === this.strCurrentLanguage)
						.forEach((pge, pageIndex) => {
							console.log(pge.page_id + " === " + pageid + " || " + pageIndex);
							if (pge.page_id === pageid) {
								this.intCurrentModule = modIndex;
								this.intCurrentScene = sceIndex;
								this.intCurrentPage = pageIndex;
								this.updateCoursePage();
							}
						});
				});
			});
			this.updateCompletedPagesInCourse();
		}

		if (pageid !== "") {
			for (let i = 0; i < this.maxModuleReached; i++) {
				this.courseStructure.moduleStructures[i].courseModule.module_status =
					"c";
				for (
					let j = 0;
					j < this.courseStructure.moduleStructures[i].childScenes.length;
					j++
				) {
					this.courseStructure.moduleStructures[i].childScenes[
						j
					].courseScene.status = "c";

					for (
						let k = 0;
						k <
						this.courseStructure.moduleStructures[i].childScenes[j].childPages
							.length;
						k++
					) {
						this.courseStructure.moduleStructures[i].childScenes[j].childPages[
							k
						].status = "c";
						/*if(this.courseStructure.moduleStructures[i].childScenes[j].childPages[k].page_id === pageid)
                        {
                            //this.courseStructure.moduleStructures[i].childScenes[j].childPages[k].status = 'c';
                            this.intCurrentModule = i;
                            this.intCurrentScene = j;
                            this.intCurrentPage = k;
                            this.updateCoursePage();
                            //break;
                        }*/
					}
				}
			}
			this.courseStructure.moduleStructures[
				this.maxModuleReached
			].courseModule.module_status = "c";
			if (
				this.courseStructure.moduleStructures[this.maxModuleReached]
					.childScenes[this.maxSceneReached].childPages.length <=
				this.maxPageReached
			)
				this.courseStructure.moduleStructures[
					this.maxModuleReached
				].childScenes[this.maxSceneReached].courseScene.status = "c";

			//if(this.maxPageReached-1 >= 0 && this.maxPageReached<=this.courseStructure.moduleStructures[this.maxModuleReached].childScenes[this.maxSceneReached].childPages.length)
			//this.courseStructure.moduleStructures[this.maxModuleReached].childScenes[this.maxSceneReached].childPages[this.maxPageReached-1].status = 'i';
			for (let i = 0; i < this.maxSceneReached; i++) {
				this.courseStructure.moduleStructures[
					this.maxModuleReached
				].childScenes[i].courseScene.status = "c";
				for (
					let j = 0;
					j <
					this.courseStructure.moduleStructures[this.maxModuleReached]
						.childScenes[i].childPages.length;
					j++
				) {
					if (
						j <
						this.courseStructure.moduleStructures[this.maxModuleReached]
							.childScenes[i].childPages.length
					)
						this.courseStructure.moduleStructures[
							this.maxModuleReached
						].childScenes[i].childPages[j].status = "c";
					else {
						for (let i = 0; i < this.maxPageReached; i++) {
							this.courseStructure.moduleStructures[
								this.maxModuleReached
							].childScenes[this.maxSceneReached].childPages[i].status = "c";
						}
					}
				}
			}
			this.courseStructure.moduleStructures[this.maxModuleReached].childScenes[
				this.maxSceneReached
			].courseScene.status = "i";
			/*for(let i=0;i<this.maxPageReached;i++){
                this.courseStructure.moduleStructures[this.maxModuleReached].childScenes[this.maxSceneReached].childPages[i].status = 'c';
            }*/
			let bookmark = TrackingManager.instance.trackingVO.bookmark;
			let modules = bookmark.split(";");
			modules.forEach((mod) => {
				let modDetails = mod.split(",");
				if (modDetails[0] == AppConstants.INCOMPLETE) {
					this.courseStructure.moduleStructures.forEach((mod, modIndex) => {
						mod.childScenes.forEach((sce, sceIndex) => {
							if (sce.courseScene.scene_id == modDetails[2]) {
								let pageID: any = modDetails[1];
								if (pageID > 0) {
									if (pageID == 1)
										this.courseStructure.moduleStructures[
											this.maxModuleReached
										].childScenes[sceIndex].childPages[pageID - 1].status = "c";
									else {
										for (let i = 0; i < pageID; i++) {
											let module = this.courseStructure.moduleStructures[this.maxModuleReached]
                                    		if(module && module.childScenes[sceIndex] &&  module.childScenes[sceIndex].childPages[i])
											{
												this.courseStructure.moduleStructures[
													this.maxModuleReached
												].childScenes[sceIndex].childPages[i].status = "c";
											}
										}
									}
								}
							}
						});
					});
				}
			});

			this.updateCompletedPagesInCourse();
		}
	}
	public updateModuleAndScene(moduleNum: number, sceneNum: number): void {
		if (this.registerJumptoPage) {
			this.registerJumptoPage =
				moduleNum !== this.intCurrentModule ||
				this.intCurrentScene !== sceneNum ||
				this.intCurrentPage > 0
					? false
					: true;
		}
		this.intCurrentModule = moduleNum;
		this.intCurrentScene = sceneNum;
		this.intCurrentPage = 0;
		this.intCurrentPageOfTotalPagesInCourse =
			this.courseStructureModel.getTotalPagesUptilCurrModuleOrScene(
				this.intCurrentModule,
				this.intCurrentScene,
				this.intCurrentPageOfTotalPagesInCourse
			);
		this.updateCoursePage();
		// Sending Module started statement
		this.createXapiStatements("started");
		this.checkForDeletedPages("next")
	}
	public updateCoursePageByIndex(
		pageid,
		moduleNum: number,
		sceneNum: number,
		pageNum: number
	): void {
		this.pageId = pageid;
		this.intCurrentJumptoPageModule = moduleNum;
		this.intCurrentJumptoPageScene = sceneNum;
		this.intCurrentJumptoPagePage = pageNum; //(pageNum - 1);
		this.registerJumptoPage = true;
		console.log(
			"Course Status ",
			this.maxModuleReached +
				" || " +
				this.maxSceneReached +
				" || " +
				this.maxPageReached
		);
	}
	public updateCoursePage(): void {
		if (
			this.registerJumptoPage &&
			this.isBackBtnClick == false &&
			this.isNextBtnClick == true
		) {
			this.courseStructureModel.updateStatusForJumpToPage(this.pageId);
			this.updateCompletedPagesInCourse();
			this.registerJumptoPage = false;
			this.intCurrentModule = this.intCurrentJumptoPageModule;
			this.intCurrentScene = this.intCurrentJumptoPageScene;
			this.intCurrentPage = this.intCurrentJumptoPagePage;

			console.log("this.intCurrentPage", this.intCurrentPage);
		} else if (
			this.registerJumptoPage &&
			this.isBackBtnClick == true &&
			this.isNextBtnClick == false
		) {
			this.intCurrentPage = 0;
		}
		this.isBackBtnClick = false;
		this.isNextBtnClick = false;
		const courseVO: CourseContext =
			this.courseStructureModel.getCourseModuleDetails(
				this.intCurrentModule,
				this.intCurrentScene,
				this.intCurrentPage
			);
		//
		if (this.intCurrentModule > this.maxModuleReached) {
			this.maxModuleReached = this.intCurrentModule;
			if (this.intCurrentScene > this.maxSceneReached)
				this.maxSceneReached = this.intCurrentScene;
			else this.maxSceneReached = 0;

			if (this.intCurrentPage > this.maxPageReached)
				this.maxPageReached = this.intCurrentPage;
			else this.maxPageReached = 0;
		} else {
			if (this.intCurrentModule >= this.maxModuleReached) {
				if (this.intCurrentScene > this.maxSceneReached) {
					this.maxSceneReached = this.intCurrentScene;
					this.maxPageReached = 0;
				} else {
					if (this.intCurrentPage - 1 >= this.maxPageReached) {
						this.maxPageReached = this.intCurrentPage;
					}
				}
			}
		}
		console.log(
			"Course Status ",
			this.maxModuleReached +
				" || " +
				this.maxSceneReached +
				" || " +
				this.maxPageReached
		);
		/* Below line of code checks & sets the name of next rubric that will be loaded.
        It is required to set the 'Next' button state incase the next rubric is a Certificate screen.*/
		ApplicationModel.instance.nextExpectedRubric = UtilityScripts.getRubricName(
			this.nextExpectedRubric().coursePage.page_rubric
		);
		/* ---------------------------------------------------------------------- */
		//
		this._notifier.next(courseVO);
	}
	public next(): void {
		this.updateCompletedPagesInCourse();
		if (this.intCurrentPage < this.totalPages - 1) {
			++this.intCurrentPage;
			++this.intCurrentPageOfTotalPagesInCourse;
			this.isNextBtnClick = true;
			this.checkForSkippablePages();
		} else if (
			this.courseStructureModel.moduleHasScene(
				this.intCurrentModule,
				this.intCurrentScene + 1
			)
		) {
			// end of a scene
			this.courseStructureModel.setSceneComplete(
				this.intCurrentModule,
				this.intCurrentScene
			);
			++this.intCurrentScene;
			++this.intCurrentPageOfTotalPagesInCourse;
			this.intCurrentPage = 0;
			this.isNextBtnClick = true;
			this.checkForSkippablePages();
		} else if (
			this.courseStructureModel.courseHasModule(this.intCurrentModule + 1)
		) {
			// check for next module
			this.createXapiStatements("completed"); //module 'completed'
			this.courseStructureModel.setSceneComplete(
				this.intCurrentModule,
				this.intCurrentScene
			);
			this.courseStructureModel.setModuleComplete(this.intCurrentModule);
			++this.intCurrentModule;
			this.intCurrentScene = 0;
			++this.intCurrentPageOfTotalPagesInCourse;
			this.intCurrentPage = 0;
			this.isNextBtnClick = true;
			this.checkForSkippablePages();
			this.createXapiStatements("started"); //module 'started'
		}
		this._disableBackBtn = false;
		this.checkForDeletedPages("next");
	}
	private createXapiStatements(verb) {
		let trackingObj = TrackingManager.instance.trackingObj;
		if (trackingObj instanceof CMI5) {
			trackingObj.API.sendStatements(
				trackingObj.createStatements(this.getStatementDataVO(verb))
			);
		}
	}
	private getStatementDataVO(verb): StatementDataVO {
		const courseContext = this.courseStructureModel.getCourseModuleDetails(
			this.intCurrentModule,
			this.intCurrentScene,
			this.intCurrentPage
		);
		this.courseData = courseContext;
		return new StatementDataVO(verb, this, true); // Return populated statementDataVO instance
	}
	private checkForSkippablePages() {
		const courseVO: CourseContext =
			this.courseStructureModel.getCourseModuleDetails(
				this.intCurrentModule,
				this.intCurrentScene,
				this.intCurrentPage
			);
		if (courseVO.coursePage.skippable) {
			courseVO.coursePage.skippable = false;
			this.next();
		} else {
			this.updateCoursePage();
		}
	}

	private checkForDeletedPages(navigaton){
		const courseVO: CourseContext =
			this.courseStructureModel.getCourseModuleDetails(
				this.intCurrentModule,
				this.intCurrentScene,
				this.intCurrentPage
			);
		if(courseVO.coursePage.deleted){
			switch(navigaton){
				case "back":
					this.back();
					break;
				case "next":
					let isFirstPage = false;
					if(this.intCurrentPage === 0){
						isFirstPage = true;
					}
					this.next();
					if(isFirstPage){
						this._disableBackBtn = true;
					}
					break;
			}
		}
	}
	/*
        This method is fired from app-right-container.component.
        It returns the current rubric that will be loaded.
        We restrict the Certificate rubric to be loaded if the user has taken less time to finish the course.
    */
	public currentRubric(): CourseContext {
		let nextIntCurrentPage = this.intCurrentPage;
		let nextIntCurrentScene = this.intCurrentScene;
		let nextIntCurrentModule = this.intCurrentModule;
		let tmpTotalPages = this.totalPages;
		if (nextIntCurrentPage < tmpTotalPages - 1) {
			++nextIntCurrentPage;
		} else if (
			this.courseStructureModel.moduleHasScene(
				nextIntCurrentModule,
				nextIntCurrentScene + 1
			)
		) {
			// end of a scene
			++nextIntCurrentScene;
			nextIntCurrentPage = 0;
		} else if (
			this.courseStructureModel.courseHasModule(nextIntCurrentModule + 1)
		) {
			// check for next module
			++nextIntCurrentModule;
			nextIntCurrentScene = 0;
			nextIntCurrentPage = 0;
		}
		const courseVO: CourseContext =
			this.courseStructureModel.getCourseModuleDetails(
				nextIntCurrentModule,
				nextIntCurrentScene,
				nextIntCurrentPage
			);
		return courseVO;
	}
	/*
	 */
	public nextExpectedRubric(): CourseContext {
		let nextIntCurrentPage = this.intCurrentPage;
		let nextIntCurrentScene = this.intCurrentScene;
		let nextIntCurrentModule = this.intCurrentModule;
		let tmpTotalPages = this.totalPages;
		if (nextIntCurrentPage < tmpTotalPages - 1) {
			++nextIntCurrentPage;
		} else if (
			this.courseStructureModel.moduleHasScene(
				nextIntCurrentModule,
				nextIntCurrentScene + 1
			)
		) {
			// end of a scene
			++nextIntCurrentScene;
			nextIntCurrentPage = 0;
		} else if (
			this.courseStructureModel.courseHasModule(nextIntCurrentModule + 1)
		) {
			// check for next module
			++nextIntCurrentModule;
			nextIntCurrentScene = 0;
			nextIntCurrentPage = 0;
		}
		//const courseVO: CourseContext = this.courseStructureModel.getCourseModuleDetails(nextIntCurrentModule, nextIntCurrentScene, nextIntCurrentPage);
		//
		let courseVO: CourseContext = new CourseContext();
		if (
			this.courseStructure.moduleStructures[nextIntCurrentModule] &&
			this.courseStructure.moduleStructures[nextIntCurrentModule]
				.courseModule &&
			this.courseStructure.moduleStructures[nextIntCurrentModule].childScenes[
				nextIntCurrentScene
			].courseScene &&
			this.courseStructure.moduleStructures[nextIntCurrentModule].childScenes[
				nextIntCurrentScene
			].childPages.length
		) {
			courseVO.courseModule =
				this.courseStructure.moduleStructures[
					nextIntCurrentModule
				].courseModule;
			courseVO.courseScene =
				this.courseStructure.moduleStructures[nextIntCurrentModule].childScenes[
					nextIntCurrentScene
				].courseScene;
			courseVO.coursePage = this.courseStructure.moduleStructures[
				nextIntCurrentModule
			].childScenes[nextIntCurrentScene].childPages.filter(
				(x) => x.page_language === this.strCurrentLanguage
			)[nextIntCurrentPage];
		}
		//
		return courseVO;
	}
	public back(): void {
		if (this.intCurrentPage > 0) {
			--this.intCurrentPage;
			--this.intCurrentPageOfTotalPagesInCourse;
			this.isBackBtnClick = true;
			this.registerJumptoPage = false;
			this.updateCoursePage();
		}
		this.checkForDeletedPages("back");
		const courseVO: CourseContext =
			this.courseStructureModel.getCourseModuleDetails(
				this.intCurrentModule,
				this.intCurrentScene,
				this.intCurrentPage-1
			);
		if(courseVO.coursePage.deleted){
			this._disableBackBtn = true;
		}
	}

	public pageComplete(event: string) {
		const currPage =
			this.courseStructureModel.courseStructureVO.moduleStructures[
				this.currentModule
			].childScenes[this.currentScene].childPages[this.currentPage];
		this.courseStructureModel.courseStructureVO.moduleStructures[
			this.currentModule
		].childScenes[this.currentScene].childPages.filter((childPage) => {
			if (childPage.page_id === currPage.page_id) {
				childPage.status = event;
			}
		});
		//this.updateCompletedPagesInCourse();
	}
	public getCurrentDocs(): CustomDoc[] {
		return this.customDocs.filter(
			(doc) => doc.language === this.currentLanguage
		);
	}

	public courseLastPage(): boolean {
		if (
			this.currentModule ===
				this.courseStructureModel.courseStructureVO.moduleStructures.length -
					1 &&
			this.currentScene ===
				this.courseStructureModel.courseStructureVO.moduleStructures[
					this.currentModule
				].childScenes.length -
					1
		) {
			return (
				this.courseStructureModel.courseStructureVO.moduleStructures[
					this.currentModule
				].childScenes[this.currentScene].childPages.filter(
					(page) => page.page_language === this.currentLanguage
				).length ===
				this.currentPage + 1
			);
		}
		return false;
	}
	public updateCompletedPagesInCourse() {
		this.intTotalCompletedpagesCount = 0;
		for (let i = 0; i < this.courseStructure.moduleStructures.length; i++) {
			for (
				let j = 0;
				j < this.courseStructure.moduleStructures[i].childScenes.length;
				j++
			) {
				for (
					let k = 0;
					k <
					this.courseStructure.moduleStructures[i].childScenes[j].childPages
						.length;
					k++
				) {
					if (
						this.courseStructure.moduleStructures[i].childScenes[j].childPages[
							k
						].page_language === this.strCurrentLanguage
					) {
						if (
							this.courseStructure.moduleStructures[i].childScenes[
								j
							].childPages[k].page_rubric.indexOf("multiplequiz") > -1
						) {
							if (
								this.courseStructure.moduleStructures[i].childScenes[j]
									.childPages[k].actual_status == "c"
							) {
								this.intTotalCompletedpagesCount++;
							} else if (
								this.courseStructure.moduleStructures[i].childScenes[j]
									.childPages[k].status == "c"
							) {
								this.intTotalCompletedpagesCount++;
							}
						} else if (
							this.courseStructure.moduleStructures[i].childScenes[j]
								.childPages[k].status == "c"
						) {
							this.intTotalCompletedpagesCount++;
						}
					}
				}
				//this.intTotalCompletedpagesCount+= this.courseStructure.moduleStructures[i].childScenes[j].childPages.filter(x => x.page_language === this.strCurrentLanguage && x.status=='c'  ).length;
			}
		}
		console.log(
			"Total Completed Page in Course ",
			this.intTotalCompletedpagesCount
		);
	}
	public get intCompletedPagesCount(): number {
		return this.intTotalCompletedpagesCount;
	}
	public get disableBackBtn(){
		return this._disableBackBtn;
	}
	public markPageAsCompletedAndSkip(arrPageList, dontskip = false) {
		this.courseStructure.moduleStructures.forEach((module) => {
			module.childScenes.forEach((scene) => {
				scene.childPages.forEach((page) => {
					if (
						page.page_id == arrPageList &&
						ApplicationModel.instance.mode != AppConstants.DEMO_MODE
					) {
						page.status = "c";
						if (!dontskip) page.skippable = true;
					}
				});
			});
		});
	}
}
